// services/itemIssue.service.js
//
// Member half of the member-answerable collection-issue loop (Phase 2 + the
// interaction-v2 additions). Backend contract — verify against, do NOT reshape:
//   ../backend/_bmad-output/sync/briefs/2026-07-21-member-answerable-collection-issues.md
//   ../backend/_bmad-output/sync/briefs/2026-07-24-member-issue-interaction-v2.md
//   ../backend/app/JsonApi/V1/ItemIssues/{ItemIssueResource,ItemIssueSchema}.php
//   ../backend/app/Http/Controllers/Api/V1/ItemIssueController.php
//   ../backend/app/Services/Commerce/ItemIssueService.php (recordMemberResponse docblock)
//
//   GET  /api/v1/item-issues?include=orderBoxItem        → the member's own
//        issue history (v2; server-scoped to their boxes by
//        ItemIssueSchema::indexQuery — a member can never enumerate another's)
//   GET  /api/v1/item-issues/{id}?include=orderBoxItem   → the full card, now
//        readable in ANY status (v2 — `show` no longer 403s after answering)
//   POST /api/v1/item-issues/{id}/-actions/respond       → record OR REVISE the
//        answer. body (plain JSON): { response, payload }
//
// Uses jsonApiClient (the single shared axios instance) so the Sanctum SPA
// cookie + X-XSRF-TOKEN + 401/419 interceptors flow through unchanged, and the
// JSON:API DataResponse is jsona-deserialized to a flat object. Do NOT create a
// new axios instance (see httpConfig/config/axios.js — multiple session cookies
// → "400 Request Header Too Large").
import { jsonApiClient } from '@/httpConfig/config/axios.js';

/**
 * The member re-opened an issue that is no longer awaiting them — the leader
 * resolved it, or the collection round moved on (policy 403 / 404, or the
 * 409 not-awaiting race backstop). NOT a bug to retry: the surface shows a
 * calm "already handled" end-state and refreshes. Mirrors the frozen brief:
 * "A 403 on respond after a leader-apply race is expected."
 */
export const ITEM_ISSUE_ALREADY_HANDLED = 'item-issue-already-handled';

/** A blocking respond outcome (plan closed / not allowed / over budget) whose
 *  member-facing Arabic message the backend already authored — surface it. */
export const ITEM_ISSUE_BLOCKED = 'item-issue-blocked';

/**
 * The JSON:API error `code` the backend puts on the ONE 409 that means "this
 * answer can never land": the question's plan was CANCELLED (or deleted). Since
 * close-now (2026-09-24) a COMPLETED plan answers 200, so a 409 no longer means
 * "the day closed" — only this code may lock her buttons. Asked for in the
 * FE→BE brief `2026-09-24-plan-closed-error-code.md`; until the backend sends
 * it, no 409 locks, which fails open (she can retry and reads the backend's own
 * message again).
 */
export const ITEM_ISSUE_PLAN_CLOSED = 'plan-closed';

function extractJsonApiDetail(error) {
  return error?.response?.data?.errors?.[0]?.detail || null;
}

function extractJsonApiCode(error) {
  return error?.response?.data?.errors?.[0]?.code || null;
}

/**
 * Normalize an axios error into a plain Error the composable can branch on:
 *  - `.status`  the HTTP status (or null on a network/timeout failure)
 *  - `.detail`  the backend's member-facing Arabic message (errors[0].detail)
 *  - `.apiCode` the backend's machine code (errors[0].code), e.g.
 *               ITEM_ISSUE_PLAN_CLOSED; null when it sent none
 *  - `.code`    ITEM_ISSUE_ALREADY_HANDLED (403/404 — resolved-elsewhere race)
 *               or ITEM_ISSUE_BLOCKED (409/422 — surface the backend detail)
 *               or null (network / 5xx — retryable, no member-facing detail)
 */
function normalizeError(error) {
  const status = error?.response?.status ?? null;
  const detail = extractJsonApiDetail(error);
  const normalized = new Error(detail || error?.message || 'Request failed');
  normalized.status = status;
  normalized.detail = detail;
  normalized.apiCode = extractJsonApiCode(error);
  normalized.original = error;

  if (status === 403 || status === 404) {
    normalized.code = ITEM_ISSUE_ALREADY_HANDLED;
  } else if (status === 409 || status === 422) {
    normalized.code = ITEM_ISSUE_BLOCKED;
  } else {
    normalized.code = null; // network / 5xx → retryable
  }
  return normalized;
}

function extractResource(response) {
  // jsonApiClient runs jsona deserialization → response.data.data is the flat
  // issue (attributes spread + id), with `orderBoxItem` nested when included.
  const data = response?.data?.data;
  if (!data) {
    throw new Error('Empty response from item-issues endpoint');
  }
  return data;
}

/**
 * Fetch the member's OWN issue history (v2 index route). Tenancy is enforced
 * server-side by ItemIssueSchema::indexQuery, so there is no member/box filter
 * to pass — and none to forget.
 *
 * NO `page[...]` PARAMS, deliberately: ItemIssueSchema declares no
 * `pagination()`, so the vendor's AllowedPageParameters rule permits an empty
 * key set and ANY page param is a 400. The route returns the member's whole
 * history in one shot. See the FE→BE brief
 * `2026-07-25-item-issue-index-pagination-and-member-payload-echo.md` — when a
 * paginator lands, thread the page params + `links` through here.
 *
 * @returns {Promise<{ issues: object[], meta: object|null, links: object|null }>}
 * @throws Error with `code`/`status`/`detail` set (see normalizeError).
 */
export async function fetchMemberIssues() {
  try {
    const response = await jsonApiClient.get('/item-issues', {
      // Newest first — `createdAt` is the schema's sortable field.
      params: { include: 'orderBoxItem', sort: '-createdAt' },
    });
    const data = response?.data?.data;
    return {
      issues: Array.isArray(data) ? data : [],
      meta: response?.data?.meta ?? null,
      links: response?.data?.links ?? null,
    };
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * Fetch the full member-visible issue for the decision card. Readable in ANY
 * status since v2, so callers MUST branch on `status` / `memberResponse` (see
 * isIssueAnswerable) instead of assuming a successful fetch means answerable.
 *
 * @param {string|number} id
 * @returns {Promise<object>} flat issue: { id, reason, question, photoUrl,
 *   options, status, allowedResponses, memberResponse, responseChannel,
 *   memberOptionIndex, memberComment, memberPhotoUrl, timeline, createdAt,
 *   orderBoxItem }.
 *
 *   Reshaped by the 2026-08-10 redesign (brief
 *   `2026-08-10-item-issue-redesign-question-and-three-answers.md`): `question`
 *   is the leader's own words and replaces `message` plus the assembled
 *   per-reason line; `options[]` — `{index, label, priceCents, qty}` — replaces
 *   `offeredVariants` / `newPriceCents` / `oldPriceCents` / `availableQty`, each
 *   of which was meaningful for exactly one reason; `memberOptionIndex`
 *   replaces `memberChosenVariantId` / `memberSuggestedSupplierText`; and
 *   `allowedResponses` is now a subset of yes / no / wait.
 *
 *   `reason` still ships with unchanged values but is a REPORTING TAG — do not
 *   branch card layout or the response set on it.
 * @throws Error with `code`/`status`/`detail` set (see normalizeError).
 */
export async function fetchItemIssue(id) {
  try {
    const response = await jsonApiClient.get(`/item-issues/${id}`, {
      params: { include: 'orderBoxItem' },
    });
    return extractResource(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * Record — or REVISE — the member's answer. `response` MUST be one of the issue's
 * `allowedResponses` (yes / no / wait). `payload` is the WIRE shape:
 * `{ option_index?, note?, photo_token? }` — `option_index` must index into the
 * issue's `options[]` (anything else is a 422) and is required on a `yes` to a
 * question that offers any; `note` is optional on every answer and is
 * round-tripped back as `memberComment` plus onto the matching `you-replied`
 * timeline step.
 *
 * `photo_token` (BE→FE 2026-08-11) is the FilePond `serverId` string from
 * `POST /api/v1/filepond` — the SAME two-step contract wallet-request receipts
 * and registration already use, so no multipart and no new endpoint. It is
 * single-use and expires after 60 minutes; a consumed, expired or forged token
 * is a 422, deliberately never a silent drop. Read back as `memberPhotoUrl`.
 * A client-supplied `photo_path` is IGNORED by the server (2026-08-11 security
 * fix — it was a path-traversal read of the whole `space` disk); never send one.
 *
 * Callers should build this via `useItemIssue`'s `respond()`, which owns the
 * camelCase→snake_case mapping and the omit-when-blank rules.
 *
 * Latest-wins: re-posting while the issue is `awaiting_member` (first answer) or
 * `open` with an answer already recorded (revision, before the leader applies)
 * overwrites memberResponse/memberComment and appends another `you-replied`
 * step. On a RESOLVED issue a DIFFERENT answer is a 409 → ITEM_ISSUE_BLOCKED,
 * while the byte-identical answer is a no-op 200 (safe retry).
 *
 * @param {string|number} id
 * @param {string} response  one of MemberIssueResponse
 * @param {object} [payload]
 * @returns {Promise<object>} the updated issue (flat, deserialized). Carries the
 *   fresh attributes (status, memberResponse, memberComment, timeline) but NO
 *   `orderBoxItem` — a custom action takes no `include`, so callers must merge
 *   onto the already-loaded issue rather than replace it.
 * @throws Error with `code`/`status`/`detail` set (see normalizeError).
 */
export async function respondToItemIssue(id, response, payload = {}) {
  try {
    const res = await jsonApiClient.post(`/item-issues/${id}/-actions/respond`, {
      response,
      payload,
    });
    return extractResource(res);
  } catch (error) {
    throw normalizeError(error);
  }
}
